/**
 * useModuleAccess.js — Phase 5 thin client for /api/access.
 *
 * Returns the caller's effective tier + a {module_id: bool} access
 * map. Cached in module scope so swapping pages doesn't re-fetch.
 *
 * Usage:
 *   const { tier, isUnlocked, requiredTier, loading } = useModuleAccess();
 *   if (!isUnlocked("patient_sim")) return <LockedModule moduleId="patient_sim" />;
 */
import { useEffect, useState } from "react";
import { api } from "./api";
import { useAuth } from "./auth";

const LS_MASTER_UNLOCK = "maldek_master_unlock_v1";

function masterUnlocked() {
  try { return localStorage.getItem(LS_MASTER_UNLOCK) === "1"; } catch { return false; }
}

let _cache = null;          // {tier, modules:{...}, matrix:{...}, fetchedAt}
let _inFlight = null;       // dedupe concurrent fetches

async function fetchAccess() {
  if (_inFlight) return _inFlight;
  _inFlight = api.get("/access/modules")
    .then((r) => {
      _cache = { ...r.data, fetchedAt: Date.now() };
      return _cache;
    })
    .finally(() => { _inFlight = null; });
  return _inFlight;
}

export function clearAccessCache() {
  _cache = null;
}

const COMMUNITY_FALLBACK = {
  tier: "community",
  tier_label: "Community (Free)",
  modules: {},
  matrix: {},
};

export function useModuleAccess() {
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(_cache);
  const [loading, setLoading] = useState(!_cache || authLoading);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (authLoading) { setLoading(true); return; }
    let cancel = false;
    setLoading(!_cache);
    fetchAccess()
      .then((d) => { if (!cancel) { setData(d); setLoading(false); } })
      .catch(() => {
        // Network/server failure → fall back to community (most-restrictive)
        if (!cancel) { setData(COMMUNITY_FALLBACK); setLoading(false); }
      });
    return () => { cancel = true; };
  }, [user, authLoading]);

  function isUnlocked(moduleId) {
    if (masterUnlocked()) return true; // founder master-code override
    if (!data) return true; // optimistic — never block on missing data
    if (!Object.prototype.hasOwnProperty.call(data.modules || {}, moduleId)) {
      return true; // unknown modules default open
    }
    return !!data.modules[moduleId];
  }

  function requiredTier(moduleId) {
    const row = data?.matrix?.[moduleId];
    if (!row) return "enterprise";
    for (const t of ["community", "pilot", "standard", "enterprise"]) {
      if (row[t]) return t;
    }
    return "enterprise";
  }

  const unlocked = masterUnlocked();
  return {
    tier:          unlocked ? "enterprise" : (data?.tier || "community"),
    tierLabel:     unlocked ? "Master Unlock" : (data?.tier_label || "Community (Free)"),
    modules:       data?.modules || {},
    matrix:        data?.matrix || {},
    isUnlocked,
    requiredTier,
    loading,
    error: err,
    masterUnlocked: unlocked,
  };
}
